import { Box, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DateTimePicker from '@/components/shared/DateTimePicker';
import ErrorPage from '@/components/shared/ErrorPage';
import ProgressBar from '@/components/shared/ProgressBar';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useProgress from '@/hooks/useProgress';
import useSearchedDestinationLabel from '@/hooks/useSearchedDestinationLabel';
import { Page } from '@/lib/tagging';
import { mapCommPref } from '@/lib/tagging/commPrefMapping';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchAppointmentSelected,
  isOnlineAppointment as isOnlineAppointmentSelected,
} from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import useAgentsAndTimeslots from '../hooks/useAgentsAndTimeslots';
import usePreloadedAgentSearchLocation from '../hooks/usePreloadedAgentSearchLocation';
import useTimeSlotSelection from '../hooks/useTimeSlotSelection';

type CalendarSelectionLayoutProps = {
  isRescheduleFlow?: boolean;
};

const CalendarSelectionLayout: React.FC<CalendarSelectionLayoutProps> = ({
  isRescheduleFlow = false,
}) => {
  const tagging = useTagging();
  const {
    timeSlots,
    isLoading: isLoadingTimeSlots,
    error: timeSlotsError,
    refetch: refetchTimeSlots,
  } = useAgentsAndTimeslots();

  const navigate = useNavigate();
  const { id } = useParams();

  useLandingPageRedirection();
  useProgress(40);
  const tagged = useRef(false);

  const {
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    handleDateChange,
    handleTimeSlotChange,
  } = useTimeSlotSelection(timeSlots);

  const selectedDestination = useAppointmentStore(
    (state) => state.selectedDestination,
  );
  const searchedDestination = useAppointmentStore(
    (state) => state.searchedDestination,
  );
  const userSearch = useAppointmentStore((state) => state.userSearch);
  const contactInfo = useAppointmentStore((state) => state.contactInfo);
  const isInBranchAppointment = useAppointmentStore(
    isInBranchAppointmentSelected,
  );
  const isOnlineAppointment = useAppointmentStore(isOnlineAppointmentSelected);
  const { sessionId, sessionSource } = useSessionStore();

  const searchedDestinationLabel = useSearchedDestinationLabel();

  usePreloadedAgentSearchLocation(isInBranchAppointment);

  useEffect(() => {
    if (!tagged.current) {
      const trvEndLocation =
        searchedDestination || userSearch || selectedDestination.name;

      tagging.tag('view', sessionSource, {
        page: Page.selectAppointment,
        sessionid: sessionId,
        flow: isRescheduleFlow ? 'modify' : '',
        trv_end_location: trvEndLocation,
        comm_pref: mapCommPref(
          contactInfo.communicationPreference,
          isInBranchAppointment,
          isOnlineAppointment,
        ),
      });

      tagged.current = true;
    }
  }, [
    searchedDestination,
    selectedDestination.name,
    sessionSource,
    sessionId,
    tagging.tag,
    contactInfo.communicationPreference,
    isInBranchAppointment,
    isOnlineAppointment,
    userSearch,
    isRescheduleFlow,
  ]);

  const handleClickContinue = () => {
    if (selectedDate && selectedStartTime && selectedEndTime) {
      const path = isRescheduleFlow
        ? '/reschedule-appointment'
        : '/schedule-appointment';
      navigate(`${path}/${id}/agent-selection`);
    }
  };

  const isButtonDisabled =
    isLoadingTimeSlots ||
    !selectedDate ||
    !selectedStartTime ||
    !selectedEndTime;

  const rescheduleTitle = 'Select a new date & time to speak with an advisor';
  const rescheduleSubtitle = 'Book an appointment';

  const regularTitle = searchedDestinationLabel
    ? `Schedule your ${searchedDestinationLabel} planning session`
    : 'Select date & time to speak with an advisor';
  const regularSubtitle = searchedDestinationLabel
    ? `Pick a time to speak with an advisor who specializes in ${searchedDestinationLabel} vacations.`
    : 'Book an appointment';

  if (timeSlotsError) {
    return <ErrorPage onClickTryAgain={refetchTimeSlots} />;
  }

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
        }}
      >
        <Stack
          spacing={6}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            pt: 4,
            minHeight: '100%',
          }}
        >
          <Stack
            spacing={0}
            sx={{
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography variant='h4'>
              {isRescheduleFlow ? rescheduleTitle : regularTitle}
            </Typography>
            <Typography
              variant='b1Regular'
              sx={{
                fontSize: {
                  xs: '1.125rem',
                },
              }}
            >
              {isRescheduleFlow ? rescheduleSubtitle : regularSubtitle}
            </Typography>
          </Stack>

          <DateTimePicker
            timeSlots={timeSlots}
            selectedDate={selectedDate}
            selectedTimeSlot={{
              startTime: selectedStartTime || '',
              endTime: selectedEndTime || '',
            }}
            onChangeDate={handleDateChange}
            onChangeTimeSlot={handleTimeSlotChange}
            isLoading={isLoadingTimeSlots}
          />
        </Stack>
      </Box>

      <ProgressBar
        buttonDisabled={isButtonDisabled}
        displayBackButton={true}
        onClickContinue={handleClickContinue}
      />
    </Stack>
  );
};

export default CalendarSelectionLayout;
